<template>
  <div
    data-testid="q-avatar"
    class="flex shrink-0 select-none items-center justify-center bg-contain bg-center text-center font-bold text-white"
    :class="[
      {
        'bg-white': background !== 'inherit' && !isBot,
        'border-white': borderColor !== 'inherit',
        'border-2': !noBorder,
      },
      isBot ? 'bg-purple-40 rounded-md' : 'rounded-full',
    ]"
    :style="dynamicStyles"
    aria-hidden="true"
  >
    <!-- Image used to detect failure-to-load of div background image -->
    <bot-icon v-if="isBot" :class="size >= 32 ? 'icon-m' : 'icon-s'" />

    <img
      v-else-if="showBackgroundImage"
      style="display: none"
      :alt="username"
      :src="src || missingUserImage"
      @error="imgError = true"
    />
    <template v-else>
      {{ userInitials }}
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, type StyleValue } from 'vue'
import BotIcon from '@/assets/bot.svg'
import missingUserImage from '@/assets/no-user.svg?raw'
import type { StringWithSuggestions } from '@/types/util'
import { getInitials } from './initials'

const props = withDefaults(
  defineProps<{
    username?: string
    size?: number
    src?: string | null
    parser?: (name: string, parser: (name: string) => string) => string
    background?: StringWithSuggestions<'inherit'>
    borderColor?: StringWithSuggestions<'inherit'>
    noBorder?: boolean
    isBot?: boolean
  }>(),
  {
    username: '',
    size: 50,
    src: '',
    parser: getInitials,
    background: '',
    borderColor: undefined,
  },
)

const imgError = ref(false)

watch(
  () => props.src,
  () => (imgError.value = false),
)

const backgroundColors = [
  'var(--brand-40)',
  'var(--brand-50)',
  'var(--blue-40)',
  'var(--blue-50)',
  'var(--pink-40)',
  'var(--pink-50)',
  'var(--orange-40)',
  'var(--orange-50)',
  'var(--yellow-40)',
  'var(--yellow-50)',
  'var(--green-40)',
  'var(--green-50)',
  'var(--teal-40)',
  'var(--teal-50)',
  'var(--purple-40)',
  'var(--purple-50)',
]

const backgroundColor = computed(
  () => props.background || randomBackgroundColor(props.username?.length || 0, backgroundColors),
)

const imageLoadedSuccessfully = computed(() => !imgError.value && !!props.src)

const showBackgroundImage = computed(() => {
  const showUserFallbackImage = (!userInitials.value && !props.isBot) || props.username === 'Anonymous user'

  return imageLoadedSuccessfully.value || showUserFallbackImage
})

const dynamicStyles = computed(() => {
  const style: StyleValue = {
    width: `${props.size}px`,
    height: `${props.size}px`,
  }
  const imgBackgroundStyle: StyleValue = {
    backgroundImage: !props.isBot && `url('${props.src || missingUserImage}')`,
    backgroundRepeat: 'no-repeat',
    borderColor: props.borderColor === 'inherit' ? undefined : 'var(--white)',
  }
  const initialBackgroundAndFontStyle: StyleValue = {
    backgroundColor: backgroundColor.value === 'inherit' || props.isBot ? undefined : backgroundColor.value,
    fontSize: `${Math.floor(props.size / 2.5)}px`,
  }

  const backgroundAndFontStyle = showBackgroundImage.value ? imgBackgroundStyle : initialBackgroundAndFontStyle

  return {
    ...style,
    ...backgroundAndFontStyle,
  }
})

const userInitials = computed(() =>
  !imageLoadedSuccessfully.value && props.username ? props.parser(props.username, getInitials) : '',
)

function randomBackgroundColor(seed: number, colors: string[]) {
  return colors[seed % colors.length]
}
</script>
